import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import clsx from 'clsx';
import toast from 'react-hot-toast';

import { getBootcamp, updateBootcamp } from '../../api/bootcampApi';
import Card    from '../../components/ui/Card';
import Spinner from '../../components/ui/Spinner';

export default function EditBootcampPage() {
  const { id }   = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();

  useEffect(() => {
    getBootcamp(id)
      .then((res) => {
        const b = res.data?.data ?? res.data;
        reset({
          title:       b.title       ?? '',
          cohort_name: b.cohort_name ?? '',
          description: b.description ?? '',
          start_date:  b.start_date  ?? '',
          end_date:    b.end_date    ?? '',
          status:      b.status      ?? 'draft',
        });
      })
      .catch(() => setError('Bootcamp not found.'))
      .finally(() => setLoading(false));
  }, [id, reset]);

  const onSubmit = async (data) => {
    setSaving(true);
    try {
      const payload = { ...data };
      if (!payload.cohort_name) payload.cohort_name = null;
      if (!payload.description) payload.description = null;
      if (!payload.start_date)  payload.start_date  = null;
      if (!payload.end_date)    payload.end_date     = null;

      await updateBootcamp(id, payload);
      toast.success('Bootcamp updated!');
      navigate('/bootcamps');
    } catch (err) {
      const errs = err?.response?.data?.errors ?? {};
      if (Object.keys(errs).length) {
        Object.values(errs).flat().forEach((e) => toast.error(e));
      } else {
        toast.error(err?.response?.data?.message ?? 'Failed to update bootcamp');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Spinner size="lg" className="text-primary-600" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex min-h-[50vh] flex-col items-center justify-center gap-3">
        <p className="text-sm text-error-500">{error}</p>
        <Link to="/bootcamps" className="text-sm text-primary-600 underline">Back to Bootcamps</Link>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6 p-6">
      <div>
        <Link
          to="/bootcamps"
          className="inline-flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-700 transition-colors"
        >
          ← Back to Bootcamps
        </Link>
        <h1 className="mt-3 text-2xl font-bold font-heading text-neutral-900">Edit Bootcamp</h1>
      </div>

      <Card>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">

          {/* Title */}
          <div>
            <label className="label-base" htmlFor="title">
              Title <span className="text-error-500">*</span>
            </label>
            <input
              id="title"
              {...register('title', {
                required: 'Title is required',
                minLength: { value: 3, message: 'Minimum 3 characters' },
                maxLength: { value: 200, message: 'Maximum 200 characters' },
              })}
              className={clsx('input-base', errors.title && 'border-error-400 focus:border-error-500 focus:ring-error-500')}
            />
            {errors.title && <p className="mt-1 text-xs text-error-600">{errors.title.message}</p>}
          </div>

          {/* Cohort name */}
          <div>
            <label className="label-base" htmlFor="cohort_name">
              Cohort Name <span className="text-neutral-400 text-xs font-normal">(optional)</span>
            </label>
            <input
              id="cohort_name"
              {...register('cohort_name', {
                maxLength: { value: 150, message: 'Maximum 150 characters' },
              })}
              className={clsx('input-base', errors.cohort_name && 'border-error-400')}
            />
            {errors.cohort_name && (
              <p className="mt-1 text-xs text-error-600">{errors.cohort_name.message}</p>
            )}
          </div>

          {/* Description */}
          <div>
            <label className="label-base" htmlFor="description">
              Description <span className="text-neutral-400 text-xs font-normal">(optional)</span>
            </label>
            <textarea
              id="description"
              rows={3}
              {...register('description')}
              className="input-base resize-none"
            />
          </div>

          {/* Dates */}
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
              <label className="label-base" htmlFor="start_date">Start Date</label>
              <input id="start_date" type="date" {...register('start_date')} className="input-base" />
            </div>
            <div>
              <label className="label-base" htmlFor="end_date">End Date</label>
              <input id="end_date" type="date" {...register('end_date')} className="input-base" />
            </div>
          </div>

          {/* Status */}
          <div>
            <label className="label-base" htmlFor="status">Status</label>
            <select id="status" {...register('status')} className="input-base">
              <option value="draft">Draft</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="archived">Archived</option>
            </select>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 border-t border-neutral-100 pt-4">
            <Link
              to="/bootcamps"
              className="rounded-button border border-neutral-200 bg-white px-4 py-2 text-sm font-medium text-neutral-700 hover:bg-neutral-50 transition"
            >
              Cancel
            </Link>
            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-button bg-primary-600 px-5 py-2 text-sm font-semibold text-white shadow-button hover:bg-primary-700 disabled:opacity-60 transition"
            >
              {saving && <Spinner size="sm" className="text-white" />}
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </div>
        </form>
      </Card>
    </div>
  );
}
